import React, { useState } from "react";
import { Button } from "components/ui";
import { useTranslation } from "lib/i18n/use-translation";
import { splitQuestionTitle } from "./questionTitle";
import AssessmentUpliftHeader from "./AssessmentUpliftHeader";
import ProgressPill, { ProgressPillProps } from "./ProgressPill";
import AssessmentStepTransition from "./AssessmentStepTransition";
import styles from "./AssessmentQuestionUplift.module.scss";
import numberStyles from "./AssessmentQuestionNumberUplift.module.scss";

export interface AssessmentQuestionNumberUpliftProps {
  stepKey: string;
  titleHtml?: string | null;
  min?: number | null;
  max?: number | null;
  defaultValue?: number;
  onSubmit: (value: string) => void;
  onBack?: () => void;
  onClose: () => void;
  progressPill?: ProgressPillProps | null;
}

const DEFAULT_MAX = 99;
const MAX_DIGITS = 2;

const AssessmentQuestionNumberUplift: React.FC<
  AssessmentQuestionNumberUpliftProps
> = ({
  stepKey,
  titleHtml,
  min,
  max,
  defaultValue,
  onSubmit,
  onBack,
  onClose,
  progressPill,
}) => {
  const { t } = useTranslation(["assessment"]);
  const { eyebrow, title } = splitQuestionTitle(titleHtml);
  const floor = min ?? 0;
  const ceiling = max ?? DEFAULT_MAX;
  const [value, setValue] = useState(defaultValue ?? floor);
  const [inputText, setInputText] = useState(String(defaultValue ?? floor));

  const clamp = (v: number) => Math.min(ceiling, Math.max(floor, v));

  const commit = (v: number) => {
    setValue(v);
    setInputText(String(v));
  };

  const decrement = () => commit(clamp(value - 1));
  const increment = () => commit(clamp(value + 1));

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = event.target.value
      .replace(/[^0-9]/g, "")
      .slice(0, MAX_DIGITS);
    setInputText(digitsOnly);
  };

  const handleInputBlur = () => {
    commit(inputText === "" ? floor : clamp(Number(inputText)));
  };

  return (
    <div className={styles.container} data-testid="assessment-question-uplift">
      <AssessmentUpliftHeader onBack={onBack} onClose={onClose} />

      {progressPill && (
        <div className={styles["pill-row"]}>
          <ProgressPill {...progressPill} />
        </div>
      )}

      <div className={styles.body}>
        <AssessmentStepTransition stepKey={stepKey}>
          <div className={styles.content}>
            {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
            <h1 className={styles.title}>{title}</h1>

            <div
              className={numberStyles.counter}
              data-testid="assessment-number-counter"
            >
              <button
                type="button"
                aria-label="decrease"
                className={numberStyles["counter-button"]}
                disabled={value <= floor}
                onClick={decrement}
              >
                {"−"}
              </button>
              <input
                type="text"
                inputMode="numeric"
                maxLength={MAX_DIGITS}
                aria-label="number value"
                className={numberStyles["counter-value"]}
                value={inputText}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
              />
              <button
                type="button"
                aria-label="increase"
                className={numberStyles["counter-button"]}
                disabled={value >= ceiling}
                onClick={increment}
              >
                {"+"}
              </button>
            </div>

            <Button
              variant="enclosed-primary"
              monochromeColour="black"
              className={numberStyles["continue-button"]}
              onClick={() => onSubmit(String(value))}
            >
              {t("assessment:question.continue")}
            </Button>
          </div>
        </AssessmentStepTransition>
      </div>
    </div>
  );
};

export default AssessmentQuestionNumberUplift;
